import { useEffect, useMemo, useState } from "react";
import { fetchLiveArticle, type LiveArticleDto } from "../api";
import { ResearchMatrixTable, type MatrixColumn } from "../components/ResearchMatrixTable";
import { PairedBarChart, type PairedBarRow } from "../components/PairedBarChart";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../indexRotation.css";
import "../macroFactors.css";

const SLUG = "macro-factors";
const CANONICAL = "https://tickerpicker.in/macro-factors";

interface Row {
  security_id: string;
  feature: string;
  label: string;
  contemporaneous_r: number | null;
  contemporaneous_n: number | null;
  no_pairing_reason: string | null;
  predictive_ic: number | null;
  predictive_threshold: number | null;
  predictive_n: number | null;
  cleared_permutation: boolean;
  wf_folds_beat_base: number | null;
  wf_folds_total: number | null;
}

interface MacroResult {
  as_of: string;
  horizon: number;
  universe: string[];
  features: Record<string, string>;
  rows: Row[];
  n_tests: number;
  n_cleared_permutation: number;
  n_cleared_walk_forward: number;
}

function indexLabel(sid: string): string {
  return sid.replace(/^IDX:/, "");
}

function fmtR(v: number | null): string {
  if (v === null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(3)}`;
}

function fmtIc(v: number | null): string {
  if (v === null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(4)}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

export function MacroFactorsPage() {
  const [data, setData] = useState<LiveArticleDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chartIndex, setChartIndex] = useState<string>("IDX:NIFTY");

  // Humanized-voice rewrite signed off 2026-10-28 (reviewed on an
  // isolated preview build, flipped together with the rest of the Market
  // Myths batch). Live data fetching, matrix/chart wiring, and the
  // factorScope computation are unchanged from the original build --
  // only the hardcoded English copy changed register.

  useEffect(() => {
    fetchLiveArticle(SLUG)
      .then(setData)
      .catch(() => setError("Could not load this article's live data."));
  }, []);

  const result = data?.result as unknown as MacroResult | undefined;

  useSeoTags({
    title: "The dollar and the market move together. That's not the same as one predicting the other. — Tickerpicker",
    description: "A living, daily-recomputed test of the dollar, US interest rates, volatility and cross-market " +
      "moves against NSE indices. Real correlation, near-zero forecasting power -- and why those are not the " +
      "same claim.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: result
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "The dollar and the market move together. That's not the same as one predicting the other.",
          description: "A living, daily-recomputed macro-correlation-vs-prediction test on NSE indices.",
          url: CANONICAL,
          publisher: { "@type": "Organization", name: "Tickerpicker" },
          isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
        }
      : undefined,
  });

  const matrixColumns: MatrixColumn[] = [
    { key: "security_id", label: "Index" },
    { key: "label", label: "Factor" },
    { key: "contemporaneous_r", label: "Contemporaneous r" },
    { key: "predictive_ic", label: "Predictive IC" },
    { key: "verdict", label: "Verdict" },
  ];

  const matrixRows = useMemo(() => {
    if (!result) return [];
    return result.rows.map((row) => ({
      security_id: indexLabel(row.security_id),
      label: row.label,
      contemporaneous_r: row.contemporaneous_r,
      predictive_ic: row.predictive_ic,
      verdict: !row.cleared_permutation
        ? "busted"
        : row.wf_folds_total
          ? `nominal only (${row.wf_folds_beat_base}/${row.wf_folds_total} folds)`
          : "nominal only",
    }));
  }, [result]);

  function matrixCellFormat(value: unknown, key: string): string {
    if (key === "contemporaneous_r") return fmtR(value as number | null);
    if (key === "predictive_ic") return fmtIc(value as number | null);
    if (value === null || value === undefined) return "—";
    return String(value);
  }

  const chartRows: PairedBarRow[] = useMemo(() => {
    if (!result) return [];
    return result.rows
      .filter((r) => r.security_id === chartIndex)
      .map((r) => ({
        label: r.label,
        valueA: r.contemporaneous_r !== null ? Math.abs(r.contemporaneous_r) : null,
        valueB: r.predictive_ic !== null ? Math.abs(r.predictive_ic) : null,
        naReasonA: r.no_pairing_reason ?? undefined,
      }));
  }, [result, chartIndex]);

  const factorScope = useMemo(() => {
    if (!result) return { total: 0, paired: 0, levelOnly: 0 };
    const total = Object.keys(result.features).length;
    const oneIndexRows = result.rows.filter((r) => r.security_id === result.universe[0]);
    const levelOnly = oneIndexRows.filter((r) => r.no_pairing_reason !== null).length;
    return { total, paired: total - levelOnly, levelOnly };
  }, [result]);

  return (
    <div className="mm-shell">
      <nav className="mm-nav">
        <a href="/" className="mm-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <a href="/market-myths">Market Myths</a>
        </div>
      </nav>

      <article className="mm-evidence ir-article">
        <div className="mm-kick">Market Myths: Tested — living article</div>
        <h1 className="mm-evidence-title">The dollar and the market move together. That's not the same as one predicting the other.</h1>

        <PageHelper>
          This page recomputes itself every day against the real, growing database. Unlike this project's other
          living articles, macro data genuinely updates daily — daily is the real cadence here, not manufactured
          freshness.
        </PageHelper>

        {error && <div className="mm-error">{error}</div>}
        {!data && !error && <div className="mm-loading">Loading…</div>}

        {data && result && (
          <>
            {data.last_run_failed && (
              <div className="ir-banner ir-banner-error">
                The most recent daily recompute failed — showing the last successful result instead.
              </div>
            )}
            {data.stale && !data.last_run_failed && (
              <div className="ir-banner ir-banner-stale">
                This result is older than its {data.cadence_days}-day refresh cadence — a recompute is overdue
                but hasn't landed yet. Showing the last successful result.
              </div>
            )}

            <div className="ir-asof-badge">
              <span className="ir-asof-dot" />
              <b>Live — recomputed daily from real data.</b> As of {fmtDate(data.computed_at)} ·{" "}
              {result.universe.length} indices · {Object.keys(result.features).length} macro factors
            </div>

            <p className="ir-invite">
              Everyone feels macro drives the market — the dollar, interest rates, global volatility, overseas
              markets overnight. We tested that directly against NSE indices: the dollar, the US 10-year
              interest rate (India's own rate couldn't be tested — see the scoping note below), India and US
              volatility (VIX), and major overseas equity markets (S&amp;P 500, Nikkei, Hang Seng). Two
              genuinely different questions, measured side by side:{" "}
              <FieldHelp term="contemporaneous_correlation" /> does a factor move together with the index in the
              same window, and <FieldHelp term="macro_predictive_ic" /> does it forecast the index's next one.
              The co-movement is often real and substantial. The forecasting power isn't —{" "}
              <b>correlation isn't prediction.</b>
            </p>

            <h2 className="ir-h2">What genuinely moves together</h2>
            <p className="ir-p">
              India VIX's own daily swings and major overseas markets' weekly moves correlate strongly with NSE
              indices in the same window — real, measurable, not surprising. The chart below puts that right
              next to the forecasting side.
            </p>

            <h2 className="ir-h2">Every index-factor pair, sortable</h2>
            <p className="ir-p">
              Sort by Contemporaneous r to see what co-moves most; sort by Predictive IC — or Verdict — to see
              that none of it forecasts. {result.n_cleared_permutation} of {result.n_tests} pairs nominally
              cleared the permutation significance floor; {result.n_cleared_walk_forward} of those also passed
              walk-forward replication. For context only — not the same count — the project's broader
              Investigation 2 (a wider pool: macro, market breadth, FII/DII flow, VIX regime conditions, and
              momentum together) found 4 of 322 nominally clear, none surviving walk-forward. This page is
              scoped to macro factors alone.
            </p>

            <ResearchMatrixTable
              rows={matrixRows}
              columns={matrixColumns}
              rowKey={(r) => `${r.security_id}__${r.label}`}
              cellFormat={matrixCellFormat}
              exportFilename={`macro_factors_${result.as_of}.csv`}
              chartSlot={() => (
                <div className="mf-chartblock">
                  <h2 className="ir-h2">The gap — co-movement vs. forecast, one index at a time</h2>
                  <p className="ir-p">
                    Pick an index. The top bar for each factor shows how strongly it moves with the index this
                    window; the bottom bar shows how well it predicts the index's next window. Watch the top
                    bars reach out while the bottom bars stay near zero — that gap is "correlation isn't
                    prediction," on this project's own real data.
                  </p>
                  <label className="rmt-label" style={{ marginBottom: 10 }}>
                    Index
                    <select value={chartIndex} onChange={(e) => setChartIndex(e.target.value)}>
                      {result.universe.map((sid) => (
                        <option key={sid} value={sid}>
                          {indexLabel(sid)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <PairedBarChart
                    rows={chartRows}
                    labelA="Contemporaneous |r| (same window)"
                    labelB="Predictive |IC| (next window)"
                    colorA="#3467c9"
                    colorB="#c97a1f"
                    formatValue={(v) => v.toFixed(3)}
                  />
                </div>
              )}
            />

            <h2 className="ir-h2">What was actually tested</h2>
            <p className="ir-p">
              {factorScope.total} macro-derived factors × {result.universe.length} indices (NIFTY and six
              liquid/differentiated sector indices — Bank, IT, Metal, Pharma, Auto, FMCG), the same universe and
              the same h=10-day forward-return method as this project's Investigation 2. Oil was never one of
              the tested factors — checked directly before building this page; the real pool is
              USD/INR, the Dollar Index (DXY), the US 10-year yield, US and India VIX, and S&amp;P
              500/Nikkei/Hang Seng as overseas proxies. {factorScope.paired} of the {factorScope.total} are
              change-based and get a genuine same-window pairing against the index's own return;{" "}
              {factorScope.levelOnly} (India VIX's raw level and its 60-day z-score) are level-based with no
              natural same-window counterpart, so they're shown predictive-only, with the reason stated inline.
            </p>
            <p className="ir-p">
              One factor is excluded outright: India's own 10-year government bond yield has zero populated days
              in this project's database (a pre-existing gap, checked directly — not something this page can
              fix), so it and the derived US-India yield spread sit out of the live test entirely, rather than
              showing up as a permanently empty row with no explanation.
            </p>

            <h2 className="ir-h2">The caveats</h2>
            <ul className="ir-storylist">
              <li><b>The co-movement is real.</b> Contemporaneous correlation is a genuine, useful descriptive fact — these things happen together, which is real context, not noise.</li>
              <li><b>It is not a forecast.</b> Knowing a factor moved doesn't tell you which way the index moves next — the predictive test is the actual claim, and it comes back null.</li>
              <li><b>Descriptive, not investment advice.</b> Neither number on this page is a trading signal.</li>
              <li><b>Narrower than the full investigation.</b> This page covers macro/cross-asset factors only — FII/DII flow has its own separately-tested null and is deliberately out of scope here, to keep this lesson clean.</li>
            </ul>

            <p className="ir-p">
              This reuses the exact independent-sampling, permutation-threshold, and walk-forward method from
              the broader index-direction investigation — see{" "}
              <a href="/market-myths?evidence=index-direction">the full methodology</a> for Investigation 2 in
              depth and the other three investigations run alongside it.
            </p>
          </>
        )}

        <div className="mm-footnote">
          This page recomputes automatically every day against the live database — the numbers above are never
          a frozen one-off snapshot. TickerPicker is a personal, non-commercial research project. Nothing here
          is investment advice.
        </div>
      </article>
    </div>
  );
}
